import { useCallback, useEffect, useRef, useState } from "react";
import useApiHttp from "../../hooks/use-http";
import { useParams } from "react-router-dom";
import {
  ActionButton,
  Button,
  Container,
  Image,
  Notifications,
} from "@jds/core";
import { downloadFile, makeZipImages } from "../../util/helper";
import { useDispatch } from "react-redux";
import GridContainer from "../Layout/GridContainer";
import Loader from "./Loader/Loader";
import { Box } from "@mui/material";
import decryptUrl from "../../util/img-decryption";
import EmptyState from "../NoData/EmptyState";
import { uploadPhotosActions } from "../../store/state-slices/photosUpload";

const PhotographerListModal = ({ data }) => {
  const { id: eventId } = useParams();
  const dispatch = useDispatch();
  const { sendRequest: getFaceList, isLoading: isGetFaceListLoading } =
    useApiHttp();
  const { sendRequest: downloadRequest } = useApiHttp();

  const [faceList, setFaceList] = useState([]);
  const [photoCount, setPhotoCount] = useState(0);
  const [page, setPage] = useState(0);
  const [lastPage, setLastPage] = useState(0);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const guestPhotoObserverRef = useRef(null);
  const guestPhotoLastElementRef = useCallback(
    (node) => {
      if (isGetFaceListLoading) return;
      if (guestPhotoObserverRef.current)
        guestPhotoObserverRef.current.disconnect();
      guestPhotoObserverRef.current = new IntersectionObserver((element) => {
        if (element[0].isIntersecting && lastPage > page) {
          setPage((pre) => pre + 1);
        }
      });
      if (node) guestPhotoObserverRef.current.observe(node);
    },
    [isGetFaceListLoading, lastPage]
  );

  const getGuestFaceListHandler = () => {
    getFaceList(
      {
        url: `face/organizer-user-photos?eventId=${eventId}&guestId=${data?.id}&page=${page}&size=25`,
        base: "MEDIA",
      },
      null,
      (data) => {
        setPhotoCount(data?.count);
        if (page == 0) {
          setFaceList(data?.data);
          setLastPage(data?.lastPage);
        } else {
          setFaceList((prev) => [...prev, ...data?.data]);
          setLastPage(data?.lastPage);
        }
      }
    );
  };
  const downloadMultipleImg = async (files) => {
    dispatch(uploadPhotosActions.setIsStarted(true));
    dispatch(uploadPhotosActions.setIsDownload(true));
    let downloadedImageBlobs = [];
    dispatch(uploadPhotosActions.setTotalCount(files.length));
    for (let i = 0; i < files.length; i++) {
      try {
        const downloadBlob = await downloadImg(i, true);
        downloadedImageBlobs.push(downloadBlob);
      } catch (err) {
      } finally {
        if (i + 1 == files.length) {
          makeZipImages(downloadedImageBlobs, "event-images");
          setDownloadLoading(false);
          dispatch(uploadPhotosActions.setTotalCount(0));
          dispatch(uploadPhotosActions.setUploadCount(0));
          dispatch(uploadPhotosActions.setIsDownload(false));
          dispatch(uploadPhotosActions.setIsStarted(false));
        }
      }
    }
  };
  const downloadImg = (index = 0, isMultiple = false) => {
    const formData = new FormData();
    formData.append("imageUrl", faceList[index]?.originalFileSignedPath);
    formData.append("eventId", eventId);
    return new Promise((resolve, reject) => {
      downloadRequest(
        {
          url: `organizer/download`,
          method: "POST",
          base: "MEDIA",
          formData: true,
          body: formData,
        },
        null,
        (data) => {
          const imageUrl = URL.createObjectURL(data);
          if (!isMultiple) {
            downloadFile(imageUrl, "image");
            Notifications.toast.success({
              id: "download-photos",
              title: "Image Downloaded",
            });
          } else {
            dispatch(uploadPhotosActions.setUploadCount(index + 1));
            resolve(data); // Resolve the Promise with the imageUrl
          }
        },
        (err, data) => {
          Notifications.toast.error({
            id: "download-photos-error",
            title: data?.message,
          });
          reject(err);
        }
      );
    });
  };
  useEffect(() => {
    getGuestFaceListHandler();
  }, [page]);
  return (
    <>
      {isGetFaceListLoading && page == 0 ? (
        <Loader size="small" />
      ) : faceList?.length ? (
        <>
          <div id="face-list-modal">
            <GridContainer
              minWidth="162px"
              maxWidth="162px"
              style={{ maxHeight: "400px", overflow: "auto" }}
            >
              {faceList.map((item, i) => (
                <div
                  ref={
                    i + 1 == faceList.length ? guestPhotoLastElementRef : null
                  }
                >
                  <Box key={i} sx={{ position: "relative" }}>
                    <Image
                      // src={item?.thumbnailFileSignedPath}
                      src={decryptUrl(item?.thumbnailFileSignedPath) || ""}
                      aspectRatio="1:1"
                    />
                    <Container
                      style={{
                        position: "absolute",
                        right: "8px",
                        bottom: "8px",
                      }}
                    >
                      <ActionButton
                        type="secondary"
                        size="small"
                        icon={"IcDownload"}
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadImg(i, false);
                        }}
                      />
                    </Container>
                  </Box>
                </div>
              ))}
              {isGetFaceListLoading && page !== 0 && (
                <Loader
                  size="small"
                  containerProps={{ style: { height: "100%" } }}
                />
              )}
            </GridContainer>
          </div>

          <Container
            horizontalAlign="center"
            fullWidth
            style={{
              marginTop: "32px",
              gap: "20px",
            }}
            direction="column"
          >
            <Button
              label={downloadLoading ? "Downloading" : "Download"}
              kind="secondary"
              size="medium"
              iconLeft="IcDownload"
              onClick={() => {
                setDownloadLoading(true);
                downloadMultipleImg(faceList);
              }}
              loading={downloadLoading}
              appearance=""
            />
          </Container>
        </>
      ) : (
        <Container fullWidth horizontalAlign="center">
          <EmptyState title="No faces found." action={false} />
        </Container>
      )}
    </>
  );
};

export default PhotographerListModal;
